import { ILocationNode } from '@app/shared/ui-components/au-locations-tree-selector/location-tree-selector.service';
import { IActionCenterLinker } from '../../interfaces';
import { Apollo } from 'apollo-angular';
import { batchUpdateConditionGrades, linkAssetsActionCenter } from './mutations';
import { firstValueFrom, timeout } from 'rxjs';
import { IClassification } from '@app/pages/cmms/interfaces/classification.interfaces';
import { ILinkAssetsActionCenterVariables } from './linking-interfaces';

import { IIncident } from '@app/pages/cmms/interfaces/incidents.interface';
import { IRequest } from '@app/pages/cmms/interfaces/request.interfaces';
import { IFileEntity } from '@app/pages/cmms/interfaces/files.interfaces';
import { IWorkOrder } from '@app/domain/cmms-types/workorder-types';
import { IConditionGradeObj } from '@app/domain/types';

export class AssetLocationLinker implements IActionCenterLinker<ILocationNode> {
	apollo: Apollo;

	constructor(deps: { apollo: Apollo }) {
		this.apollo = deps.apollo;
	}

	async link(selectedPks: string[], targets: ILocationNode[]) {
		const variables: ILinkAssetsActionCenterVariables = {
			assets: selectedPks,
			location: targets[0].locationId,
		};
		return await firstValueFrom(
			this.apollo
				.mutate({ mutation: linkAssetsActionCenter, variables })
				.pipe(timeout(5000))
		);
	}
}

export class AssetClassificationLinker implements IActionCenterLinker<IClassification> {
	apollo: Apollo;

	constructor(deps: { apollo: Apollo }) {
		this.apollo = deps.apollo;
	}

	async link(selectedPks: string[], targets: IClassification[]) {
		const variables: ILinkAssetsActionCenterVariables = {
			assets: selectedPks,
			classifications: targets.map(classification => classification.pk),
		};
		return await firstValueFrom(
			this.apollo
				.mutate({ mutation: linkAssetsActionCenter, variables })
				.pipe(timeout(5000))
		);
	}
}

export class AssetWorkOrdersLinker implements IActionCenterLinker<IWorkOrder> {
	apollo: Apollo;

	constructor(deps: { apollo: Apollo }) {
		this.apollo = deps.apollo;
	}

	async link(selectedPks: string[], targets: IWorkOrder[]) {
		const variables: ILinkAssetsActionCenterVariables = {
			assets: selectedPks,
			workOrders: targets.map(workOrder => workOrder.pk),
		};
		return await firstValueFrom(
			this.apollo
				.mutate({ mutation: linkAssetsActionCenter, variables })
				.pipe(timeout(5000))
		);
	}
}

export class AssetIncidentLinker implements IActionCenterLinker<IIncident> {
	apollo: Apollo;

	constructor(deps: { apollo: Apollo }) {
		this.apollo = deps.apollo;
	}

	async link(selectedPks: string[], targets: IIncident[]) {
		const variables: ILinkAssetsActionCenterVariables = {
			assets: selectedPks,
			incidents: targets.map(incident => incident.pk),
		};
		return await firstValueFrom(
			this.apollo
				.mutate({ mutation: linkAssetsActionCenter, variables })
				.pipe(timeout(5000))
		);
	}
}

export class AssetRequestLinker implements IActionCenterLinker<IRequest> {
	apollo: Apollo;

	constructor(deps: { apollo: Apollo }) {
		this.apollo = deps.apollo;
	}

	async link(selectedPks: string[], targets: IRequest[]) {
		const variables: ILinkAssetsActionCenterVariables = {
			assets: selectedPks,
			requests: targets.map(request => request.pk),
		};
		return await firstValueFrom(
			this.apollo
				.mutate({ mutation: linkAssetsActionCenter, variables })
				.pipe(timeout(5000))
		);
	}
}

export class AssetFileLinker implements IActionCenterLinker<IFileEntity> {
	apollo: Apollo;

	constructor(deps: { apollo: Apollo }) {
		this.apollo = deps.apollo;
	}

	async link(selectedPks: string[], targets: IFileEntity[]) {
		const variables: ILinkAssetsActionCenterVariables = {
			assets: selectedPks,
			files: targets.map(file => file.pk),
		};
		return await firstValueFrom(
			this.apollo
				.mutate({ mutation: linkAssetsActionCenter, variables })
				.pipe(timeout(5000))
		);
	}
}

export class AssetLabelLinker implements IActionCenterLinker<string> {
	apollo: Apollo;

	constructor(deps: { apollo: Apollo }) {
		this.apollo = deps.apollo;
	}

	async link(selectedPks: string[], targets: string[]) {
		const variables: ILinkAssetsActionCenterVariables = {
			assets: selectedPks,
			labels: targets,
		};
		return await firstValueFrom(
			this.apollo
				.mutate({ mutation: linkAssetsActionCenter, variables })
				.pipe(timeout(5000))
		);
	}
}

export class AssetConditionGradeLinker
	implements IActionCenterLinker<IConditionGradeObj>
{
	apollo: Apollo;

	constructor(deps: { apollo: Apollo }) {
		this.apollo = deps.apollo;
	}

	async link(selectedPks: string[], targets: IConditionGradeObj[]) {
		const variables: {
			assets: string[];
			conditionGrade: number;
			conditionGradeNote: string | null;
		} = {
			assets: selectedPks,
			...targets[0],
		};
		return await firstValueFrom(
			this.apollo
				.mutate({ mutation: batchUpdateConditionGrades, variables })
				.pipe(timeout(5000))
		);
	}
}
